import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'next-i18next'
import { type MouseEventHandler, useMemo, useRef } from 'react'
import { Button } from 'ui/src/components/Button/Button'
import { Divider } from 'ui/src/components/Divider/Divider'
import { ArrowDownIcon } from 'ui/src/icons/ArrowIcon'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { CancellationForm } from '@/components/Cancellation/CancellationForm'
import { AttentionCard, InfoInlineCard } from '@/components/InfoCard/InfoCard'
import { ProductCard } from '@/components/ProductCard/ProductCard'
import { useHasScrolledPast } from '@/components/ProductPage/ScrollPast/useHasScrolledPast'
import { useCartEntryToReplace } from '@/components/ProductPage/useCartEntryToReplace'
import { CartDiscount } from '@/features/CartDiscount/CartDiscount'
import { useSelectedOfferValueOrThrow } from '@/features/priceCalculator/PurchaseFormV2/useSelectedOffer'
import { usePriceCalculatorStep } from '@/features/priceCalculator/usePriceCalculatorStep'
import { PriceIntentNotificationType } from '@/services/graphql/generated'
import { useShopSessionIdOrThrow, useShopSessionValueOrThrow } from '@/services/shopSession/ShopSessionContext'
import { useTracking } from '@/services/Tracking/useTracking'
import { useAddToCart } from '@/utils/useAddToCart'
import { usePriceIntent } from '../../usePriceIntent'
import { priceCalculatorAddedOffer } from '../priceCalculatorAtoms'
import { SectionSubtitle } from '../SectionHeading'
import { SectionTitle } from '../SectionHeading'
import { OfferEditButton } from './OfferEditButton'
import { scrollToBottomButton } from './OfferPresenterV2.css'

export function OfferSummary() {
  const targetRef = useRef(null)
  const hasScrolledPast = useHasScrolledPast({ targetRef, offset: 800 })

  const { t } = useTranslation('purchase-form')
  const shopSessionId = useShopSessionIdOrThrow()
  const selectedOffer = useSelectedOfferValueOrThrow()
  const { priceIntent, resetPriceIntent } = usePriceIntent()
  const entryToReplace = useCartEntryToReplace()
  const setAddedOffer = useSetAtom(priceCalculatorAddedOffer)
  const { setStep } = usePriceCalculatorStep()

  const tracking = useTracking()

  const [addToCart, loadingAddToCart] = useAddToCart({
    shopSessionId,
    entryToReplace: entryToReplace?.id,
    onSuccess(productOfferId) {
      if (!priceIntent) {
        return
      }

      const addedProductOffer = priceIntent.offers.find((offer) => offer.id === productOfferId)

      if (addedProductOffer === undefined) {
        throw new Error(`Unknown offer added to cart: ${productOfferId}`)
      }

      resetPriceIntent()
      tracking.reportAddToCart(addedProductOffer, 'store')
      window.scrollTo({ top: 0, behavior: 'instant' })
      setAddedOffer(addedProductOffer)
      // Reset price calculator step to initial state
      setStep('loadingForm')
    },
  })

  const handleAddToCart: MouseEventHandler = async () => {
    await addToCart(selectedOffer.id)
  }

  const productOfferIds = useMemo(() => {
    if (!priceIntent) {
      return []
    }

    return priceIntent.offers.map(({ id }) => id)
  }, [priceIntent])

  const shopSession = useShopSessionValueOrThrow()

  const handleScrollToBottom = () => {
    window.scrollTo(0, window.document.body.scrollHeight)
  }

  if (!priceIntent) {
    return null
  }

  return (
    <section>
      <SectionTitle>{t('OFFER_PRESENTER_SUMMARY_TITLE')}</SectionTitle>
      <SectionSubtitle className={sprinkles({ marginBottom: 'lg' })}>
        {t('OFFER_PRESENTER_SUMMARY_SUBTITLE')}
      </SectionSubtitle>
      <ProductCard.Root offer={selectedOffer}>
        <ProductCard.Header />

        <ProductCard.Details>
          <OfferEditButton />
        </ProductCard.Details>

        <CancellationForm
          productOfferIds={productOfferIds}
          offer={selectedOffer}
          minStartDate={priceIntent.minStartDate}
        />

        {shopSession.cart.campaignsEnabled && (
          <>
            <CartDiscount shopSession={shopSession} />
            <Divider />
          </>
        )}

        <div ref={targetRef}>
          <ProductCard.Breakdown />
        </div>

        <ProductCard.TotalPrice />

        <div className={yStack({ alignItems: 'stretch', gap: 'xs', mt: 'xs' })}>
          {priceIntent.notifications?.map(({ type, message }, index) => {
            const InfoCardComponent = type === PriceIntentNotificationType.Warning ? AttentionCard : InfoInlineCard
            return <InfoCardComponent key={index}>{message}</InfoCardComponent>
          })}

          <Button variant="primary" size="large" onClick={handleAddToCart} loading={loadingAddToCart} fullWidth={true}>
            {t('ADD_TO_CART_BUTTON_LABEL')}
          </Button>
        </div>
      </ProductCard.Root>

      <Button
        onClick={handleScrollToBottom}
        className={clsx(scrollToBottomButton.base, hasScrolledPast && scrollToBottomButton.hidden)}
        size="small"
        variant="secondary"
        Icon={<ArrowDownIcon />}
        aria-label={t('SCROLL_TO_BOTTOM', { ns: 'common' })}
      />
    </section>
  )
}
